/**
 * View-model builders: SimState → HudState. All the exact numbers the tone
 * contract demands live here, next to nothing else.
 */

import abilitiesJson from '../content/abilities.json' with { type: 'json' };
import type { AbilityId } from '../sim/types.ts';
import towersJson from '../content/towers.json' with { type: 'json' };
import { TICK_RATE } from '../sim/const.ts';
import { CORE_POWER, placeableDef, tierCaps, TIER_COSTS, towerStats } from '../sim/defs.ts';
import type { Sim } from '../sim/sim.ts';
import { SPECIES } from '../sim/species.ts';
import type { DepthPalette } from '../render/palette.ts';
import type { RimShoal, Structure } from '../sim/types.ts';
import type { AbilityButton, Banner, HotbarItem, HudState, InspectorLine, PathRow } from '../ui/hud.ts';
import { BUILD_ORDER, HOTBAR } from './hotbar.ts';
import { entityIcon, entityThumb } from './thumbs.ts';

const ABILITY_IDS: AbilityId[] = ['q', 'w', 'e', 'r'];

/** Icon side length inside a hotbar button (doc 17 §8: 34 px button, 4 px rim). */
const HOTBAR_ICON = 28;

export interface HudInputs {
  sim: Sim;
  /** Palette the thumbnails are rendered in — the depth being played. */
  depth: DepthPalette;
  selectedStructId: number;
  selectedShoalId: string | null;
  tool: string | null;
  hotbarHover: number;
  /** Which ability is in aim mode, if any (doc 07 click-to-aim). */
  aiming: AbilityId | null;
  abilityHover: number;
  banner: Banner | null;
  toast: { text: string; slide: number } | null;
  fast: boolean;
  debug: string;
  /** Doc 08: exact rim counts are a meta unlock; without it, cards estimate. */
  exactCounts?: boolean;
}

export function buildHudState(inp: HudInputs): HudState {
  const { sim } = inp;
  const r = sim.state.resources;
  const core = sim.byId(sim.state.coreId)!;
  const rate = (v: number): number => Math.round(v * 10) / 10;

  // a live boss (or mini-boss) puts its health front and centre
  let boss: HudState['boss'] = null;
  for (const e of sim.state.enemies) {
    if (SPECIES[e.species]!.boss) {
      boss = { name: SPECIES[e.species]!.name, hp: e.hp, maxHp: e.maxHp };
      break;
    }
  }

  // wages at the whistle: the "rate" chip previews NEXT PAYDAY, not a per-second flow
  const payday = sim.forecast();
  return {
    resources: [
      { label: 'ORE', value: Math.floor(r.ore), rate: rate(payday.ore) },
      { label: 'ALLOY', value: Math.floor(r.alloy), rate: rate(payday.alloy) },
      // doc 17 §7: an empty magazine says so, in the colour of a problem
      {
        label: r.ammo < 1 && sim.phase === 'combat' ? 'AMMO — DRY' : 'AMMO',
        value: Math.floor(r.ammo),
        rate: rate(payday.ammo),
        ...(r.ammo < 1 && sim.phase === 'combat' ? { alarm: true } : {}),
      },
      { label: 'GAS', value: Math.floor(r.gas), rate: rate(payday.gas) },
      { label: 'FUEL', value: Math.floor(r.fuel), rate: rate(payday.fuel) },
      { label: 'CORE', value: Math.ceil(core.hp) },
    ],
    power: { demand: sim.power.demand, supply: Math.round(sim.power.supply) },
    capacitor: Math.floor(sim.state.capacitor),
    fights: { cleared: sim.state.fightsCleared, total: sim.state.rim.length },
    hotbar: HOTBAR.map((h, k): HotbarItem => {
      const def = BUILD_ORDER[k]!;
      const allowed = sim.mods.allowedDefs;
      const locked = allowed !== null && !allowed.includes(def);
      const item: HotbarItem = {
        ...h,
        icon: entityIcon(def, inp.depth, HOTBAR_ICON),
        tooltip: { ...h.tooltip, thumb: entityThumb(def, inp.depth) },
        affordable:
          !locked &&
          Object.entries(placeableDef(def)!.cost).every(
            ([res, amt]) => r[res as keyof typeof r] >= (amt ?? 0),
          ),
      };
      if (locked) {
        item.locked = true;
        item.tooltip = { ...item.tooltip, cost: 'LOCKED — BUY IT IN COLONY COMMAND' };
      }
      return item;
    }),
    hotbarHover: inp.hotbarHover,
    hotbarSelected: inp.tool ? BUILD_ORDER.indexOf(inp.tool as (typeof BUILD_ORDER)[number]) : -1,
    inspector: inspector(inp),
    abilities: ABILITY_IDS.map((id) => abilityButton(inp, id)),
    abilityHover: inp.abilityHover,
    boss,
    banner: inp.banner,
    toast: inp.toast,
    fast: inp.fast,
    debug: inp.debug,
  };
}

/** One ability slot: price, cooldown sweep, and whether it is even owned. */
function abilityButton(inp: HudInputs, id: AbilityId): AbilityButton {
  const { sim } = inp;
  const def = abilitiesJson.abilities[id];
  const cost = def.cost as { ammo?: number; alloy?: number; capacitor?: number };
  const allowed = sim.mods.allowedAbilities;
  const locked = allowed !== null && !allowed.includes(id);
  const cdTicks = sim.state.abilityReady[id] - sim.state.tick;
  const price =
    cost.ammo !== undefined
      ? `${cost.ammo} AMMO`
      : cost.alloy !== undefined
        ? `${cost.alloy} ALLOY`
        : `${cost.capacitor} CHARGE`;
  const have =
    cost.ammo !== undefined
      ? sim.state.resources.ammo >= cost.ammo
      : cost.alloy !== undefined
        ? sim.state.resources.alloy >= cost.alloy
        : sim.state.capacitor >= (cost.capacitor ?? 0);
  const out: AbilityButton = {
    label: SHORT[id],
    hotkey: def.key,
    cost: price,
    cooldown: cdTicks > 0 ? cdTicks / (def.cooldown * TICK_RATE) : 0,
    affordable: !locked && have,
    aiming: inp.aiming === id,
    tooltip: {
      name: def.name,
      hotkey: def.key,
      effect: def.line,
      cost: locked ? 'LOCKED — BUY IT IN COLONY COMMAND' : `${price} · ${def.cooldown}S COOLDOWN`,
    },
  };
  if (locked) out.locked = true;
  return out;
}

/** Four characters is what a 34 px button holds — the full name is in the tooltip. */
const SHORT: Record<AbilityId, string> = { q: 'RAIN', w: 'ZAP', e: 'MEND', r: 'PUSH' };

function inspector(inp: HudInputs): HudState['inspector'] {
  const { sim } = inp;
  const shoal = inp.selectedShoalId
    ? sim.state.rim.find((x) => x.id === inp.selectedShoalId)
    : undefined;
  if (shoal) return shoalCard(shoal, inp.exactCounts === true);
  const st = inp.selectedStructId >= 0 ? sim.byId(inp.selectedStructId) : undefined;
  if (!st) return null;
  if (st.kind === 'tower') return towerCard(sim, st);
  return structureCard(sim, st);
}

/** Doc 08 Fish Whisperer: exact counts are bought; until then, you squint. */
function crowd(count: number): string {
  return count <= 1 ? 'ONE OF THEM' : count <= 3 ? 'A FEW' : count <= 9 ? 'A CROWD' : 'TOO MANY';
}

function shoalCard(shoal: RimShoal, exact: boolean): HudState['inspector'] {
  const comp = new Map<string, number>();
  for (const g of shoal.composition) comp.set(g.species, (comp.get(g.species) ?? 0) + g.count);
  const lines: InspectorLine[] = [];
  for (const [species, count] of comp) {
    lines.push({
      label: SPECIES[species]?.name ?? species.toUpperCase(),
      value: exact ? `x${count}` : crowd(count),
    });
  }
  if (shoal.gimmick) lines.push({ label: 'GIMMICK', value: shoal.gimmick, tone: 'critical' });
  lines.push({
    label: 'PATIENCE',
    value: shoal.riled ? 'RILED +30% HP' : '*'.repeat(shoal.patience) + '-'.repeat(3 - shoal.patience),
    tone: shoal.riled ? 'critical' : 'ink',
  });
  lines.push({ label: 'PULL', value: 'SPACE', tone: 'good' });
  const kind = shoal.boss === 'boss' ? 'THE BOSS' : shoal.boss === 'miniboss' ? 'MINI-BOSS' : 'CIRCLING';
  return {
    title: shoal.name,
    subtitle: `MENACE ${'*'.repeat(shoal.menace)} · ${kind}`,
    lines,
  };
}

function towerCard(sim: Sim, st: Structure): HudState['inspector'] {
  const d = placeableDef(st.def)!;
  const t = (towersJson.towers as Record<string, { tagline?: string; paths?: Record<string, { name: string }> }>)[st.def];
  const stats = sim.state.resources; // alloy for affordability
  const lines: InspectorLine[] = [];
  if (st.disabledUntil > sim.state.tick) {
    lines.push({ label: 'STATUS', value: 'GRABBED — BEING HUGGED', tone: 'critical' });
  }
  const live = towerLiveStats(st);
  for (const l of live) lines.push(l);
  lines.push({ label: 'HP', value: `${Math.ceil(st.hp)}/${st.maxHp}`, tone: st.hp < st.maxHp / 2 ? 'warning' : 'good' });
  lines.push({ label: 'SELL', value: 'X', tone: 'warning' });

  // the 3×3 (doc 05): row = path, with the sim's own legality rules mirrored
  const paths: PathRow[] = [];
  if (!st.ruin && t?.paths) {
    for (const key of ['a', 'b', 'c'] as const) {
      const tier = st.upgrades[key];
      const others = (['a', 'b', 'c'] as const).filter((p) => p !== key);
      const invested = others.filter((p) => st.upgrades[p] > 0).length;
      let blocked: string | undefined;
      const levels = sim.mods.towerLevels;
      const gated = levels !== null && tier + 1 > tierCaps(levels[st.def] ?? 1)[key];
      if (tier >= 3) blocked = 'MAX';
      else if (tier === 0 && invested >= 2) blocked = 'PICK 2';
      else if (tier === 2 && others.some((p) => st.upgrades[p] >= 3)) blocked = 'CAP 1';
      else if (gated) blocked = `LVL ${key === 'a' ? 2 : key === 'b' ? 3 : tier === 0 ? 4 : 5}`;
      const nextCost = tier < 3 && !blocked ? TIER_COSTS[tier]! : null;
      const row: PathRow = {
        key,
        name: t.paths[key]!.name,
        tier,
        nextCost,
        buyable: nextCost !== null && stats.alloy >= nextCost,
      };
      if (blocked) row.blocked = blocked;
      paths.push(row);
    }
  }
  return {
    title: st.ruin ? `${d.name} (RUIN)` : d.name,
    subtitle: t?.tagline ?? d.label,
    lines,
    paths,
  };
}

/** Live effective stats after upgrades — exact numbers, always (doc 01). */
function towerLiveStats(st: Structure): InspectorLine[] {
  const s = towerStats(st.def, st.upgrades);
  const lines: InspectorLine[] = [];
  if (st.def === 'zappy_boi') {
    lines.push({ label: 'ZAP', value: `${s.damage} DMG` });
    lines.push({ label: 'CHAINS', value: `${s.chains} (−${Math.round(s.chainFalloff * 100)}%/HOP)` });
    if (s.stun > 0) lines.push({ label: 'STUN', value: `${s.stun.toFixed(1)}S` });
    lines.push({ label: 'DRINKS', value: `${s.powerPerShot} CAPACITOR`, tone: 'warning' });
  } else if (st.def === 'laser_pointer') {
    lines.push({ label: 'BEAM', value: `${s.beamDps} DMG/S` });
    lines.push({ label: 'RAMPS TO', value: `x${s.rampMult} IN ${s.rampSeconds}S` });
    if (s.beams > 1) lines.push({ label: 'BEAMS', value: `${s.beams} AT ${Math.round(s.beamSplit * 100)}%` });
    if (s.revealRadius > 0) {
      lines.push({ label: 'LIGHTS UP', value: `R${s.revealRadius} · +${Math.round(s.litAmp * 100)}% DMG` });
    }
    lines.push({ label: 'RANGE', value: `${s.range} TILES` });
    lines.push({ label: 'DRINKS', value: `${s.powerPerSecond} POWER`, tone: 'warning' });
  } else if (st.def === 'fan_enjoyer') {
    lines.push({ label: 'FIELD', value: `${Math.round(s.fieldSlow * 100)}% SLOW, R${s.fieldRadius}` });
    if (s.swiftStrip > 0) lines.push({ label: 'SWIFT', value: `STRIPPED (+${Math.round(s.swiftStrip * 100)}%)` });
    if (s.pushEvery > 0) {
      lines.push({ label: 'PUSH', value: `${s.pushTiles} TILES EVERY ${s.pushEvery}S` });
      if (s.pushDamage > 0) lines.push({ label: 'PUSH DMG', value: `${s.pushDamage}` });
    }
    if (s.pullRate > 0) lines.push({ label: 'VORTEX', value: `${s.pullRate}/S, R${s.pullRadius}` });
    if (s.clusterAmp > 0) lines.push({ label: 'CLUSTERED', value: `+${Math.round(s.clusterAmp * 100)}% SPLASH` });
    lines.push({ label: 'DRINKS', value: `${s.powerPerSecond} POWER`, tone: 'warning' });
  } else if (st.def === 'subscription_canceller') {
    lines.push({ label: 'TORPEDO', value: `${s.damage} DMG x${s.torpedoes}` });
    if (s.vsBossMult > 1) lines.push({ label: 'VS BOSSES', value: `+${Math.round((s.vsBossMult - 1) * 100)}%`, tone: 'good' });
    if (s.splashRadius > 0) {
      lines.push({ label: 'SPLASH', value: `${s.splashFull > 0 ? s.damage : s.splashDamage} IN R${s.splashRadius}` });
    }
    if (s.armorShredPct > 0) {
      lines.push({ label: 'SHREDS', value: `−${Math.round(s.armorShredPct * 100)}% ARMOR ${s.armorShredDur}S`, tone: 'good' });
    }
    lines.push({ label: 'TARGETS', value: 'THE FATTEST' });
    lines.push({ label: 'RANGE', value: `${s.range} TILES` });
    lines.push({ label: 'AMMO', value: `${s.ammoPerShot}/SHOT`, tone: 'warning' });
  } else if (st.def === 'yeet_cannon') {
    lines.push({ label: 'BOOM', value: `${s.damage} IN R${s.splashRadius}` });
    if (s.bombletCount > 0) lines.push({ label: 'BOMBLETS', value: `${s.bombletCount} x ${s.bombletDmg}` });
    if (s.knockback > 0) lines.push({ label: 'KNOCKBACK', value: `${s.knockback} TILES` });
    lines.push({ label: 'RANGE', value: `${s.minRange}–${s.range}` });
    lines.push({ label: 'AMMO', value: `${s.ammoPerShot}/SHOT`, tone: 'warning' });
  } else {
    lines.push({ label: 'DAMAGE', value: `${s.damage}${s.pierce > 1 ? ` x${s.pierce >= 99 ? 'ALL' : s.pierce}` : ''}` });
    lines.push({ label: 'RATE', value: `${s.rate.toFixed(2)}/S` });
    if (s.bleedDps > 0) lines.push({ label: 'BLEED', value: `${s.bleedDps}/S FOR ${s.bleedDur}S` });
    lines.push({ label: 'RANGE', value: `${s.range} TILES` });
    lines.push({ label: 'AMMO', value: `${s.ammoPerShot}/SHOT`, tone: 'warning' });
  }
  return lines;
}

function structureCard(sim: Sim, st: Structure): HudState['inspector'] {
  if (st.def === 'core_dome') {
    return {
      title: 'CORE DOME',
      subtitle: 'The Landlord. The lose condition.',
      lines: [
        { label: 'HP', value: `${Math.ceil(st.hp)}/${st.maxHp}`, tone: st.hp < 500 ? 'critical' : 'good' },
        { label: 'STORAGE', value: `${sim.storageCap}/RES` },
        { label: 'POWER', value: `+${CORE_POWER}` },
      ],
    };
  }
  if (st.def === 'wall') {
    return {
      title: st.ruin ? 'WALL (RUIN)' : 'KELP-CRETE WALL',
      subtitle: st.ruin ? 'Rebuild on it at 50% cost.' : 'Fish chew at 15s of wall per meal.',
      lines: [
        { label: 'HP', value: `${Math.ceil(st.hp)}/${st.maxHp}`, tone: st.hp < st.maxHp / 2 ? 'warning' : 'good' },
        { label: 'SELL', value: 'X', tone: 'warning' },
      ],
    };
  }
  // economy buildings
  const d = placeableDef(st.def)!;
  const status = st.ruin ? 'RUIN' : (sim.status.get(st.id) ?? 'stalled');
  const eff = sim.power.efficiency;
  const lines: InspectorLine[] = [
    status === 'RUIN'
      ? { label: 'STATUS', value: 'RUBBLE', tone: 'critical' }
      : status === 'offgrid'
        ? { label: 'STATUS', value: 'OFF GRID ⚡✗', tone: 'critical' }
        : status === 'working'
          ? eff < 1
            ? { label: 'STATUS', value: `BROWNOUT ${Math.round(eff * 100)}%`, tone: 'warning' }
            : { label: 'STATUS', value: 'WORKING', tone: 'good' }
          : { label: 'STATUS', value: 'STALLED — ZZZ', tone: 'warning' },
  ];
  for (const [res, r2] of Object.entries(d.consumes ?? {})) {
    lines.push({ label: `${res.toUpperCase()} IN`, value: `${r2}/ROUND` });
  }
  for (const [res, r2] of Object.entries(d.produces ?? {})) {
    lines.push({ label: `${res.toUpperCase()} OUT`, value: `${r2}/ROUND`, tone: 'good' });
  }
  if (st.depositTiles.length > 0) {
    const left = st.depositTiles.reduce((a, t) => a + (sim.state.deposits[String(t)] ?? 0), 0);
    lines.push({
      label: d.wants === 'vein' ? 'VEIN LEFT' : 'VENT LEFT',
      value: `${Math.ceil(left)}`,
      tone: left <= 0 ? 'critical' : 'ink',
    });
    if (left <= 0) lines.push({ label: 'RECYCLE', value: 'X · FULL REFUND', tone: 'good' });
  }
  if (d.power !== 0) {
    lines.push({ label: 'POWER', value: `${d.power > 0 ? '+' : ''}${d.power}`, tone: d.power > 0 ? 'good' : 'warning' });
  }
  if (d.relayRadius) lines.push({ label: 'RELAY', value: `RADIUS ${d.relayRadius}` });
  if (d.storagePerResource) lines.push({ label: 'STORAGE', value: `+${d.storagePerResource}/RES`, tone: 'good' });
  if (d.healRatePerRound) lines.push({ label: 'REPAIRS', value: `${d.healRatePerRound} HP/ROUND`, tone: 'good' });
  lines.push({ label: 'HP', value: `${Math.ceil(st.hp)}/${st.maxHp}`, tone: st.hp < st.maxHp / 2 ? 'warning' : 'good' });
  if (st.depositTiles.length === 0 || !st.depositTiles.every((t) => (sim.state.deposits[String(t)] ?? 0) <= 0)) {
    lines.push({ label: 'SELL', value: 'X', tone: 'warning' });
  }
  return { title: st.ruin ? `${d.name} (RUIN)` : d.name, subtitle: d.label, lines };
}
